import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { ActivitySummary } from "@nebulize/sdk";

import { Button, Card, EmptyState, Grid, Input, Page, Panel, Stack, Switch } from "@/components/ui";

import { QueryPanel } from "../account/components/QueryPanel";
import { AvailabilityBadge, CanvasChips, Chip } from "../components/Chips";
import { Facts, Mono, Muted } from "../components/Text";
import { MACHINE_REQUIREMENT_TYPES, boundLabel, requiredPositionsOf } from "../lib/requirements";

import {
  useCatalogSearch,
  useInterfaces,
  useRollup,
  type CapabilityFacet,
  type CapabilityQueryInput,
} from "./lib/queries";

function kindWord(entry: ActivitySummary): string {
  return entry.is_bundle === true ? "bundle" : "activity";
}

/** What the store rollup says this activity brings. One read per card, cached by activity. */
function Rollup({ activityId }: { readonly activityId: string }) {
  const rollup = useRollup(activityId);
  const data = rollup.data;
  if (data === undefined) return null;
  const positions = requiredPositionsOf(data);

  return (
    <Stack gap={4}>
      <div className="cx-brings">
        <span className="cx-key">Declares</span>
        <CanvasChips kinds={data.canvases ?? []} />
      </div>

      <div className="cx-brings">
        <span className="cx-key">Requires</span>
        {positions.length > 0 ? (
          <div className="cx-canvaslist">
            {positions.map((one) => (
              <div className="cx-canvasline" key={one.id}>
                <Mono>{one.requirement}</Mono>
                <Muted small>{boundLabel(one.min, one.max)}</Muted>
                {one.activity_id === activityId ? null : (
                  <Muted small>{`from ${one.activity_id}`}</Muted>
                )}
              </div>
            ))}
          </div>
        ) : (
          <Muted small>None</Muted>
        )}
      </div>

      <div className="cx-chips">
        <Chip>{`${String(data.configuration_field_count ?? 0)} ${
          data.configuration_field_count === 1 ? "setting" : "settings"
        }`}</Chip>
        <Chip tone={data.needs_a_machine === true ? undefined : "ns"}>
          {data.needs_a_machine === true ? "needs a machine" : "cloud only"}
        </Chip>
      </div>
    </Stack>
  );
}

/** Word an empty result names one unmatched CAT-027 facet with, so a search that finds nothing
 * says which piece was empty rather than leaving a blank grid to read as a typo. */
const FACET_WORD: Record<CapabilityFacet["kind"], string> = {
  requires: "requires",
  provides: "provides",
  exports: "publishes the topic",
  triggers: "accepts the trigger",
  effects: "declares the effect",
  abilities: "declares the ability",
  entity_types: "declares the entity type",
  keyed_by: "keys an entity by",
  canvas_kinds: "draws the canvas kind",
  reads: "reads",
  writes: "writes to",
};

function unmatchedLine(facet: CapabilityFacet): string {
  return `Nothing in the catalog ${FACET_WORD[facet.kind]} "${facet.value}".`;
}

const DEBOUNCE_MS = 300;
type BundlePick = "any" | "activity" | "bundle";

/** The library. Search runs server-side against CAT-027's facets: never a full fetch filtered
 * in the browser, which is the thing this screen replaced. */
export function ActivityCatalog() {
  const navigate = useNavigate();
  const interfaces = useInterfaces();

  const [rawText, setRawText] = useState("");
  const [text, setText] = useState("");
  const [canvasKinds, setCanvasKinds] = useState<string[]>([]);
  const [canvasDraft, setCanvasDraft] = useState("");
  const [machineOnly, setMachineOnly] = useState(false);
  const [bundlePick, setBundlePick] = useState<BundlePick>("any");

  useEffect(() => {
    const timer = setTimeout(() => {
      setText(rawText.trim());
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [rawText]);

  const registry = interfaces.data ?? [];
  const needle = text.toLowerCase();
  // Free text resolves against the registered vocabulary (CAT-027's `requires`, the superset of
  // registered type names) rather than the manifest text itself: a search asks what an activity
  // does, and `requires` is exact-string, so "keyboard" only ever finds `input.key.v1` through
  // the registry entry whose name or summary says so.
  const textMatches =
    needle === ""
      ? []
      : registry.filter(
          (one) =>
            one.name.toLowerCase().includes(needle) || one.summary.toLowerCase().includes(needle),
        );
  const textRequires = textMatches.map((one) => one.name);
  const noVocabularyHit = needle !== "" && textMatches.length === 0;

  const requires = machineOnly
    ? [...new Set([...textRequires, ...MACHINE_REQUIREMENT_TYPES])]
    : textRequires;
  const query: CapabilityQueryInput = {};
  if (requires.length > 0) query.requires = requires;
  if (canvasKinds.length > 0) query.canvas_kinds = canvasKinds;

  const search = useCatalogSearch(query, !noVocabularyHit);

  const addCanvasKind = (): void => {
    const value = canvasDraft.trim();
    if (value === "" || canvasKinds.includes(value)) return;
    setCanvasKinds((held) => [...held, value]);
    setCanvasDraft("");
  };

  return (
    <Page title="Activities" measure="wide">
      <Panel title="Search">
        <Stack gap={4}>
          <div className="cx-filterbar">
            <Input
              value={rawText}
              placeholder="What it does: keyboard, dice roller, vote..."
              aria-label="Search the catalog"
              onChange={(event) => {
                setRawText(event.target.value);
              }}
            />
            <Switch
              label="Needs a machine"
              checked={machineOnly}
              onChange={setMachineOnly}
            />
            <div className="cx-seg">
              {(["any", "activity", "bundle"] as const).map((value) => (
                <button
                  type="button"
                  key={value}
                  className={value === bundlePick ? "cx-seg-btn cx-seg-btn-on" : "cx-seg-btn"}
                  onClick={() => {
                    setBundlePick(value);
                  }}
                >
                  {value === "any" ? "Any kind" : value === "activity" ? "Single" : "Bundle"}
                </button>
              ))}
            </div>
          </div>
          <div className="cx-filterbar">
            <Input
              value={canvasDraft}
              placeholder="Canvas kind, then Enter"
              aria-label="Add a canvas kind filter"
              onChange={(event) => {
                setCanvasDraft(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                event.preventDefault();
                addCanvasKind();
              }}
            />
            {canvasKinds.length > 0 ? (
              <div className="cx-chips">
                {canvasKinds.map((kind) => (
                  <button
                    type="button"
                    className="cx-chip cx-chip-x"
                    key={kind}
                    onClick={() => {
                      setCanvasKinds((held) => held.filter((one) => one !== kind));
                    }}
                  >
                    {`canvas: ${kind}`}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </Stack>
      </Panel>

      {noVocabularyHit ? (
        <Panel title="Catalog">
          <EmptyState
            bare
            headline={`Nothing in the catalog vocabulary matches "${text}"`}
            body="Check the spelling, or search what it does rather than what it is called."
          />
        </Panel>
      ) : (
        <QueryPanel title="Catalog" rows={4} query={search}>
          {(data) => {
            const activities = data.pages.flatMap((page) => page.activities);
            const unmatched = data.pages.at(-1)?.unmatchedFacets ?? [];
            const visible = activities.filter(
              (entry) => bundlePick === "any" || (bundlePick === "bundle") === (entry.is_bundle === true),
            );

            if (visible.length === 0) {
              return unmatched.length > 0 ? (
                <EmptyState
                  bare
                  headline="Nothing matched"
                  body={unmatched.map(unmatchedLine).join(" ")}
                />
              ) : (
                <EmptyState bare headline="Nothing in the catalog" />
              );
            }

            return (
              <Stack gap={5}>
                <Grid minColumnWidth="360px" gap={5}>
                  {visible.map((entry) => (
                    <Card key={entry.activity_id} padding="none" aria-label={entry.name}>
                      <button
                        type="button"
                        className="cx-actcard"
                        onClick={() => {
                          void navigate({
                            to: "/console/activities/$activityId",
                            params: { activityId: entry.activity_id },
                          });
                        }}
                      >
                        <Stack gap={4}>
                          <div className="cx-actcard-head">
                            <div className="cx-actcard-name">{entry.name}</div>
                            <Chip>{kindWord(entry)}</Chip>
                            <AvailabilityBadge availability={entry.availability} />
                          </div>
                          <Muted>{entry.description ?? ""}</Muted>
                          <Facts
                            items={[
                              { label: "Activity", value: entry.activity_id, mono: true },
                              { label: "Version", value: entry.version, mono: true },
                            ]}
                          />
                          <Rollup activityId={entry.activity_id} />
                        </Stack>
                      </button>
                    </Card>
                  ))}
                </Grid>
                {search.hasNextPage ? (
                  <Button
                    variant="ghost"
                    loading={search.isFetchingNextPage}
                    onClick={() => {
                      void search.fetchNextPage();
                    }}
                  >
                    Load more
                  </Button>
                ) : null}
              </Stack>
            );
          }}
        </QueryPanel>
      )}
    </Page>
  );
}
